import { memo, useCallback, useMemo, useRef, useState } from 'react';

import { cn } from '../../lib/styles';
import { ButtonProps, ButtonSize } from '../Button';
import { Label } from '../Label';
import { Menu, MenuClose, MenuContent, MenuTrigger } from '../Menu';
import { PopoverContentProps } from '../Popover';

import { lowercaseSearch } from '../../../core/utils/string';
import { DialogInput } from '../../components/DialogInput';
import { Divider } from '../Divider';
import { MenuItem } from '../MenuItem';
import { CheckboxMenuItem, CheckboxMenuItems } from './CheckboxMenuItems';
import { RadioMenuItems } from './RadioMenuItems';
import { SelectOptionLabel, getPlainLabelText } from './SelectOptionLabel';
import { getSelectButtonText } from './Select.utils';
import { SelectTrigger } from './SelectTrigger';
import type { SelectOption, SelectVariant } from './Select.types';

export type { SelectOption, SelectVariant } from './Select.types';

export interface SelectProps<T> {
  id?: string;
  options: SelectOption<T | null | undefined>[];
  value?: T | null;
  values?: T[]; // For multiple selection
  label?: string;
  /** Names the default trigger when no visible `label` does. */
  'aria-label'?: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  buttonClassName?: string;
  className?: string;
  labelClassName?: string;
  reserveErrorSpace?: boolean;
  menuClassName?: string;
  /** Extra classes for each option label in the menu. Defaults to semibold; pass e.g. `font-normal` to override. */
  itemLabelClassName?: string;
  placeholderClassName?: string;
  multiple?: boolean;
  buttonSuffix?: React.ReactNode;
  trigger?: React.ReactNode;
  align?: PopoverContentProps['align'];
  withinPortal?: boolean;
  size?: ButtonSize;
  variant?: SelectVariant;
  searchable?: boolean;
  searchPlaceholder?: string;
  footer?: React.ReactNode;
  /**
   * Whether to show "Select All" option for multiple select
   */
  withSelectAll?: boolean;
  selectAllLabel?: string;
  /**
   * If true, automatically scroll to the active item when the select is opened
   */
  autoFocusItem?: boolean;
  /** When set, hovering the caret swaps to an X that clears the selection without opening the menu. */
  clearable?: boolean;
  onValueChange?: (value: T | null | undefined) => void;
  onValuesChange?: (
    values: T[],
    /** Whether the value was added (true) or removed (false) */
    checked: boolean,
    /** The value that was added or removed */
    value: T,
  ) => void; // For multiple selection
  onItemClick?: (option: SelectOption<T | null | undefined>, checked?: boolean) => void;
  renderTriggerLabel?: (values: T[]) => React.ReactNode;
  getSearchableText?: (option: SelectOption<T | null | undefined>) => string;
  triggerButtonVariant?: ButtonProps['variant'];
  /**
   * Custom render function for each option. When provided, bypasses the default
   * RadioMenuItems/CheckboxMenuItems rendering and gives full control over how
   * each option is displayed.
   */
  renderOption?: (
    option: SelectOption<T | null | undefined>,
    context: { isSelected: boolean; onSelect: () => void },
  ) => React.ReactNode;
  emptyOptionsBehavior?: 'hide' | 'disable';
  /** When true, string option labels are rendered as Markdown in the menu and trigger. */
  markdownLabels?: boolean;
  /** When true, aligns the control to the top of multi-line labels in the menu. */
  alignLabelStart?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onOpenAutoFocus?: PopoverContentProps['onOpenAutoFocus'];
  onCloseAutoFocus?: PopoverContentProps['onCloseAutoFocus'];
  onMenuMouseDown?: React.MouseEventHandler<HTMLDivElement>;
}

function SelectBase<T>({
  id,
  size,
  variant = 'default',
  options,
  value,
  values,
  label,
  'aria-label': ariaLabel,
  placeholder = 'Select option',
  error,
  disabled = false,
  className,
  labelClassName,
  reserveErrorSpace = true,
  buttonClassName,
  menuClassName,
  itemLabelClassName,
  placeholderClassName,
  multiple = false,
  searchable = false,
  searchPlaceholder = 'Search',
  buttonSuffix,
  trigger,
  align = 'start',
  withinPortal = false,
  footer,
  withSelectAll,
  selectAllLabel = 'Select All',
  autoFocusItem = true,
  clearable = false,
  onValueChange,
  onValuesChange,
  onItemClick,
  renderTriggerLabel,
  getSearchableText,
  triggerButtonVariant = 'secondary',
  renderOption,
  emptyOptionsBehavior = 'hide',
  markdownLabels = false,
  alignLabelStart = false,
  open: openProp,
  onOpenChange,
  onOpenAutoFocus,
  onCloseAutoFocus,
  onMenuMouseDown,
}: SelectProps<T>) {
  const [search, setSearch] = useState('');
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isOpenControlled = openProp !== undefined;
  const open = isOpenControlled ? openProp : uncontrolledOpen;

  const setOpen = useCallback(
    (nextOpen: boolean) => {
      if (!isOpenControlled) {
        setUncontrolledOpen(nextOpen);
      }

      onOpenChange?.(nextOpen);
    },
    [isOpenControlled, onOpenChange],
  );
  const menuContentRef = useRef<HTMLDivElement>(null);

  const selectedOption = useMemo(() => {
    if (multiple) return null;

    return options.find((option) => option.value === value);
  }, [options, value, multiple]);

  const filteredOptions = useMemo(() => {
    if (!search?.trim()) return options;

    return options.filter((option) => {
      const searchableText = getSearchableText?.(option) ?? getPlainLabelText(option.label);
      return lowercaseSearch(searchableText, search);
    });
  }, [getSearchableText, options, search]);

  const selectAllValue = useMemo(() => {
    if (!!values && !!values.length && values.length < options.length) return 'indeterminate';

    return !!values && values.length === options.length;
  }, [options.length, values]);

  const handleRadioMenuItemClick = useCallback(
    (option: SelectOption<T | null | undefined>, _event: React.MouseEvent) => {
      onItemClick?.(option);
    },
    [onItemClick],
  );

  const handleClear = useCallback(() => {
    onValueChange?.(null);
  }, [onValueChange]);

  const handleOpenAutoFocus = useCallback(
    (event: Event) => {
      onOpenAutoFocus?.(event);
      if (event.defaultPrevented) return;

      if (!autoFocusItem) return;

      const menuContentElement = event.target as HTMLDivElement;

      const selectedElement = menuContentElement.querySelector(`[data-active="true"]`) as HTMLElement | null;

      selectedElement?.scrollIntoView({ block: 'center' });
    },
    [autoFocusItem, onOpenAutoFocus],
  );

  if (emptyOptionsBehavior === 'hide' && options.length === 0) return null;

  const { text: buttonText, isShowingPlaceholder } = getSelectButtonText({
    multiple,
    selectedOption,
    placeholder,
    values,
    options,
    renderTriggerLabel,
  });

  const triggerLabel =
    markdownLabels && !isShowingPlaceholder ? (
      <SelectOptionLabel label={buttonText} markdown className="truncate" />
    ) : (
      buttonText
    );

  const renderMenuContent = () => {
    if (renderOption) {
      return (
        <>
          {filteredOptions.map((option) => {
            const isSelected = selectedOption?.value === option.value;
            return (
              <MenuClose key={String(option.value)}>
                {renderOption(option, {
                  isSelected,
                  onSelect: () => {
                    onValueChange?.(option.value);
                    onItemClick?.(option);
                  },
                })}
              </MenuClose>
            );
          })}
        </>
      );
    }

    if (multiple)
      return (
        <CheckboxMenuItems
          options={filteredOptions}
          values={values}
          onValuesChange={onValuesChange}
          onItemClick={onItemClick}
          labelClassName={itemLabelClassName}
          markdownLabels={markdownLabels}
          alignLabelStart={alignLabelStart}
        />
      );

    if (variant === 'simple')
      return (
        <>
          {filteredOptions.map((option) => (
            <MenuClose key={String(option.value)}>
              <MenuItem
                active={selectedOption?.value === option.value}
                onClick={() => {
                  onValueChange?.(option.value);
                  onItemClick?.(option);
                }}
                className={itemLabelClassName}
                data-active={option.value === value}
              >
                <SelectOptionLabel label={option.label} markdown={markdownLabels} />
              </MenuItem>
            </MenuClose>
          ))}
        </>
      );

    return (
      <RadioMenuItems
        options={filteredOptions}
        selectedOption={selectedOption}
        labelClassName={itemLabelClassName}
        markdownLabels={markdownLabels}
        alignLabelStart={alignLabelStart}
        onValueChange={onValueChange}
        onItemClick={handleRadioMenuItemClick}
      >
        {(children) => <MenuClose>{children}</MenuClose>}
      </RadioMenuItems>
    );
  };

  const menu = (
    <Menu open={open} onOpenChange={setOpen}>
      <MenuTrigger asChild>
        {trigger ?? (
          <SelectTrigger
            aria-label={ariaLabel}
            variant={triggerButtonVariant}
            disabled={disabled || (emptyOptionsBehavior === 'disable' && options.length === 0)}
            size={size}
            error={error}
            isPlaceholder={isShowingPlaceholder}
            placeholderClassName={placeholderClassName}
            suffix={buttonSuffix}
            clearable={clearable && !multiple}
            onClear={handleClear}
            className={cn(label ? buttonClassName : className || buttonClassName, {
              'text-text-tertiary': multiple ? !values || values.length === 0 : !selectedOption,
            })}
          >
            {triggerLabel}
          </SelectTrigger>
        )}
      </MenuTrigger>

      <MenuContent
        ref={menuContentRef}
        className={cn(
          'w-[var(--radix-popover-trigger-width)] max-w-xl overflow-x-hidden p-0 max-h-[446px] flex flex-col',
          {
            'overflow-y-auto': !searchable,
          },
          menuClassName,
        )}
        align={align}
        withinPortal={withinPortal}
        onOpenAutoFocus={handleOpenAutoFocus}
        onCloseAutoFocus={onCloseAutoFocus}
        onMouseDown={onMenuMouseDown}
      >
        {searchable && (
          <DialogInput placeholder={searchPlaceholder} value={search} onChange={(e) => setSearch(e.target.value)} />
        )}

        {withSelectAll && multiple && options.length > 1 && (
          <div className="flex flex-col px-1.5 pt-1.5 gap-1.5">
            <CheckboxMenuItem
              option={{
                label: selectAllLabel,
                value: selectAllValue,
              }}
              labelClassName="font-medium text-text-secondary font-body-sm"
              indeterminate={selectAllValue === 'indeterminate'}
              isChecked={selectAllValue === true}
              onItemClick={(option, checked, indeterminate) => {
                if (indeterminate || !checked) {
                  return onValuesChange?.([], checked, option.value as T);
                }
                return onValuesChange?.(options.map((option) => option.value) as T[], checked, option.value as T);
              }}
            />
            <div className="px-2.5">
              <Divider />
            </div>
          </div>
        )}

        <div className={cn('p-1.5', { 'overflow-y-auto': searchable })}>
          {renderMenuContent()}

          {searchable && filteredOptions.length === 0 && (
            <div className="flex items-center justify-center w-full h-full p-4">
              <span className="font-body-sm text-text-tertiary">Nothing found</span>
            </div>
          )}
        </div>

        {footer}
      </MenuContent>
    </Menu>
  );

  if (!label) return menu;

  return (
    <div className={cn('flex flex-col', className)}>
      <Label htmlFor={id} className={cn('mb-2', labelClassName)}>
        {label}
      </Label>

      {menu}

      {reserveErrorSpace || error ? (
        <div className="h-5 pt-1 text-text-destructive font-body-sm text-left">{error}</div>
      ) : null}
    </div>
  );
}

export const Select = memo(SelectBase) as <T>(props: SelectProps<T>) => React.ReactElement;
